<script setup lang="ts">
const config = useRuntimeConfig();
const siteOrigin = config.public.mainUrl as string;

definePageMeta({
  layout: "static",
});

useSeoMeta({
  title: "Website & Applications Terms of Use | &Dine",
  description:
    "Terms of use for the &Dine website and applications. Read the conditions that apply when using our office catering platform.",
});

useHead({
  link: [{ rel: "canonical", href: `${siteOrigin}/website-terms` }],
});
</script>

<template>
  <section class="website-terms">
    <h1 class="grey-text">&amp;Dine Website &amp; Applications Terms of Use</h1>
    <p><em>Last updated: January 2026</em></p>

    <p>
      These terms of use (together with the documents referred to in them) (
      <strong>&ldquo;Terms&rdquo;</strong>
      ) set out the basis on which you may access and use the &amp;Dine website at
      www.anddine.co.uk, any related sub-domains, and any applications made available by &amp;Dine
      (together, the
      <strong>&ldquo;Platform&rdquo;</strong>
      ), whether as a guest or a registered user.
    </p>
    <p>
      By accessing or using the Platform, placing an order, or instructing &amp;Dine to place an
      order on your behalf, you confirm that you accept these Terms and agree to be bound by them.
      If you do not agree, you must not use the Platform.
    </p>

    <p><strong>1. Information About Us</strong></p>
    <p>
      The Platform is operated by &amp;Dine Ltd, a company incorporated in England and Wales (
      <strong>&ldquo;&amp;Dine&rdquo;</strong>
      ,
      <strong>&ldquo;we&rdquo;</strong>
      ,
      <strong>&ldquo;us&rdquo;</strong>
      ,
      <strong>&ldquo;our&rdquo;</strong>
      ).
    </p>
    <p>Registered office:</p>
    <p>Beyond, Fox Court, 14 Gray&rsquo;s Inn Road, London, WC1X 8HN</p>
    <p>
      Contact:
      <a href="mailto:hello@anddine.co.uk">hello@anddine.co.uk</a>
    </p>
    <p>
      &amp;Dine operates a technology platform that enables customers to place orders with
      independent food businesses (
      <strong>&ldquo;Makers&rdquo;</strong>
      ). &amp;Dine does not prepare food and does not sell food as principal.
    </p>

    <p><strong>2. Nature of the Platform and Our Role</strong></p>
    <p><strong>2.1 Marketplace and agency role</strong></p>
    <p>
      The Platform is a marketplace. All food and related products (
      <strong>&ldquo;Products&rdquo;</strong>
      ) are prepared and supplied by independent Makers.
    </p>
    <p>
      Unless you have entered into a separate written enterprise agreement with &amp;Dine stating
      otherwise:
    </p>
    <ul>
      <li>your contract for Products is directly with the relevant Maker;</li>
      <li>
        &amp;Dine acts as the Maker&rsquo;s agent to facilitate ordering, payment processing,
        customer support, and delivery coordination; and
      </li>
      <li>&amp;Dine is not the seller, manufacturer, or provider of food.</li>
    </ul>
    <p><strong>2.2 Maker control</strong></p>
    <p>Makers:</p>
    <ul>
      <li>set their own menus, prices, availability, cut-off times, and portion sizes;</li>
      <li>control acceptance or rejection of orders;</li>
      <li>
        are solely responsible for food safety, allergen accuracy, preparation, packaging,
        labelling, and compliance with applicable laws; and
      </li>
      <li>
        may pause availability or cancel orders in accordance with their operational constraints.
      </li>
    </ul>
    <p>&amp;Dine cannot compel a Maker to accept an order.</p>

    <p><strong>3. Eligibility and Accounts</strong></p>
    <p>You must be 18 years or older to use the Platform.</p>
    <p>You may:</p>
    <ul>
      <li>place orders as a guest;</li>
      <li>create an individual account; or</li>
      <li>be added as a user within an organisation account created by a company administrator.</li>
    </ul>
    <p>Company administrators represent and warrant that they have authority to:</p>
    <ul>
      <li>create organisation accounts;</li>
      <li>add users;</li>
      <li>set budgets, permissions, and ordering rules; and</li>
      <li>permit users to place orders on behalf of the organisation.</li>
    </ul>
    <p>The organisation is responsible for all activity carried out by its users.</p>

    <p><strong>4. Access, Availability and Account Security</strong></p>
    <p>
      Access to the Platform is provided on a temporary basis. &amp;Dine reserves the right to
      withdraw, suspend, restrict, or amend the Platform (or any part of it) at any time without
      notice.
    </p>
    <p>
      &amp;Dine does not guarantee that the Platform will always be available, uninterrupted,
      secure, or error-free. We are not liable if, for any reason, the Platform is unavailable at
      any time or for any period.
    </p>
    <p>
      We may restrict access to some or all parts of the Platform to registered users, or suspend or
      terminate access where we reasonably believe there has been misuse, unauthorised activity, or
      breach of these Terms.
    </p>
    <p>
      You are responsible for maintaining the confidentiality of your login credentials and for all
      activity carried out under your account. If you believe your account has been compromised,
      misused, or accessed without authorisation, you must notify us immediately at
      <a href="mailto:hello@anddine.co.uk">hello@anddine.co.uk</a>
      .
    </p>
    <p>
      The Platform is directed to users in the United Kingdom. We do not represent that content or
      services available through the Platform are appropriate for use outside the UK.
    </p>

    <p><strong>5. Orders and Acceptance</strong></p>
    <p><strong>5.1 Order placement</strong></p>
    <p>Orders may be placed:</p>
    <ul>
      <li>directly via the Platform; or</li>
      <li>via assisted ordering (see clause 6).</li>
    </ul>
    <p>All orders are subject to:</p>
    <ul>
      <li>Maker acceptance;</li>
      <li>availability; and</li>
      <li>applicable cut-off times.</li>
    </ul>
    <p>No order is binding until accepted by the Maker.</p>

    <p><strong>5.2 Allergens and dietary information</strong></p>
    <p>
      Allergen and dietary information is provided by Makers and may be displayed on the Platform
      and/or on product labels where Makers use &amp;Dine&rsquo;s labelling tools. Makers are solely
      responsible for the accuracy, completeness, and legal compliance of all allergen, ingredient,
      and dietary information relating to their Products.
    </p>
    <p><strong>Important information:</strong></p>
    <ul>
      <li>
        Allergen and dietary information is based on information supplied by Makers and may be
        incomplete, inaccurate, or subject to change at any time.
      </li>
      <li>
        Products may be prepared in environments where cross-contamination with allergens may occur.
      </li>
      <li>
        Menus and labels may not reflect last-minute ingredient substitutions or changes made by
        Makers.
      </li>
      <li>
        While &amp;Dine takes reasonable steps to display information provided by Makers, &amp;Dine
        does not prepare food and does not independently verify allergen, ingredient, or dietary
        information.
      </li>
    </ul>
    <p>You acknowledge and agree that:</p>
    <ul>
      <li>
        you do not rely on any allergen, ingredient, or dietary information displayed on the
        Platform or communicated by &amp;Dine;
      </li>
      <li>
        you are solely responsible for ensuring that any Product is suitable for you or any
        recipient before placing an order; and
      </li>
      <li>
        where allergies, intolerances, or dietary requirements are present, you order entirely at
        your own risk.
      </li>
    </ul>
    <p>
      Where necessary, you should contact the relevant Maker directly to confirm ingredient and
      allergen information prior to ordering. &amp;Dine does not assess, advise on, or confirm
      Product suitability, including where orders are placed via Assisted Ordering.
    </p>
    <p>
      Nothing in these Terms excludes or limits any rights you may have under applicable consumer
      protection or food safety laws.
    </p>

    <p><strong>6. Assisted Ordering by &amp;Dine</strong></p>
    <p>
      From time to time, &amp;Dine may place orders on the Platform on your behalf, at your request,
      for example where an account manager enters an order based on instructions provided by email
      or other communication (
      <strong>&ldquo;Assisted Ordering&rdquo;</strong>
      ).
    </p>
    <p>Where Assisted Ordering occurs:</p>
    <ul>
      <li>&amp;Dine acts solely as an administrative agent, following your instructions;</li>
      <li>
        you remain fully responsible for the accuracy, suitability, and completeness of the order,
        including menus, quantities, dietary requirements, allergens, delivery details, and delivery
        instructions;
      </li>
      <li>
        &amp;Dine does not provide advice, recommendations, or assurances regarding Products,
        Makers, allergens, or suitability; and
      </li>
      <li>
        the order is subject to the same Terms, confirmations, limitations, and disclaimers as if
        you had placed the order directly via the Platform.
      </li>
    </ul>
    <p>
      Order confirmations and order details issued following Assisted Ordering constitute
      confirmation of your instructions and acceptance of the order as placed.
    </p>
    <p>
      For the avoidance of doubt, Clause 5.2 (Allergens and dietary information) applies in full to
      all Assisted Ordering, and &amp;Dine does not verify, assess, or confirm the suitability of
      any Product for allergies, intolerances, or dietary requirements when placing orders on your
      behalf.
    </p>

    <p><strong>7. Pricing, Fees and Payments</strong></p>
    <p>Prices for Products are set by Makers.</p>
    <p>&amp;Dine may charge:</p>
    <ul>
      <li>service fees;</li>
      <li>delivery fees;</li>
      <li>equipment fees; and</li>
      <li>staffing fees,</li>
    </ul>
    <p>which are displayed separately from Product prices.</p>
    <p>
      Payments are processed by third-party payment providers (such as Stripe) or via bank transfer.
      &amp;Dine does not store card details.
    </p>
    <p>Where &amp;Dine collects payment, it does so:</p>
    <ul>
      <li>as agent for the Maker in respect of Product prices; and</li>
      <li>as principal in respect of &amp;Dine fees.</li>
    </ul>

    <p><strong>8. Delivery</strong></p>
    <p>
      Delivery is carried out by independent third-party delivery partners engaged by &amp;Dine.
    </p>
    <p>Delivery partners may receive:</p>
    <ul>
      <li>recipient name;</li>
      <li>delivery address;</li>
      <li>telephone number; and</li>
      <li>delivery instructions,</li>
    </ul>
    <p>solely for the purpose of completing the delivery.</p>
    <p>
      A delivery is considered successful where it is delivered within the agreed time window with
      appropriate proof of delivery, which may include GPS data, timestamps, and handover
      photographs.
    </p>
    <p>&amp;Dine is not responsible for failed or delayed deliveries caused by:</p>
    <ul>
      <li>incorrect or incomplete delivery information;</li>
      <li>lack of access;</li>
      <li>recipient unavailability; or</li>
      <li>site-specific restrictions.</li>
    </ul>

    <p><strong>9. Cancellations, Refunds and Credits</strong></p>
    <p>
      If a Maker cancels an order, &amp;Dine may offer a refund or credit, depending on the
      circumstances.
    </p>
    <p>Refunds or credits for late or incomplete deliveries may be offered:</p>
    <ul>
      <li>where required under an enterprise agreement; or</li>
      <li>as a goodwill gesture at &amp;Dine&rsquo;s discretion.</li>
    </ul>

    <p><strong>10. Acceptable Use</strong></p>
    <p>You must not:</p>
    <ul>
      <li>misuse the Platform;</li>
      <li>harass Makers, delivery partners, or &amp;Dine staff;</li>
      <li>scrape, copy, or harvest data, menus, or pricing;</li>
      <li>attempt to bypass the Platform to transact directly with Makers; or</li>
      <li>use automated systems to access the Platform.</li>
    </ul>
    <p>&amp;Dine may suspend or terminate access where misuse occurs.</p>

    <p><strong>11. Intellectual Property</strong></p>
    <p>
      All intellectual property in the Platform belongs to &amp;Dine or its licensors. You may not
      copy, reproduce, or exploit any part of the Platform without permission.
    </p>

    <p><strong>12. Liability</strong></p>
    <p><strong>12.1 Food and Products</strong></p>
    <p>To the maximum extent permitted by law:</p>
    <ul>
      <li>
        &amp;Dine is not responsible for food quality, safety, suitability, allergens, or
        compliance;
      </li>
      <li>all such responsibility rests with the relevant Maker.</li>
    </ul>
    <p><strong>12.2 Limitation of liability</strong></p>
    <p>
      For non-enterprise users, &amp;Dine&rsquo;s total liability arising out of or in connection
      with any order shall be limited to the fees paid to &amp;Dine for that order (excluding the
      Product price).
    </p>
    <p>&amp;Dine shall not be liable for:</p>
    <ul>
      <li>loss of profit;</li>
      <li>loss of business;</li>
      <li>business interruption; or</li>
      <li>any indirect or consequential loss.</li>
    </ul>
    <p>
      Nothing in these Terms limits liability for death or personal injury caused by negligence,
      fraud, or any liability that cannot be excluded by law.
    </p>

    <p><strong>13. Privacy and Data Protection</strong></p>
    <p>
      Your use of the Platform is subject to our
      <NuxtLink to="/privacy-policy">Privacy Policy</NuxtLink>
      , which explains how personal data is handled, including data shared with Makers and delivery
      partners.
    </p>

    <p><strong>14. Changes to the Platform and Terms</strong></p>
    <p>
      We may update the Platform or these Terms at any time. Continued use constitutes acceptance of
      the updated Terms.
    </p>

    <p><strong>15. Governing Law and Jurisdiction</strong></p>
    <p>
      These Terms are governed by English law, and the courts of England and Wales shall have
      exclusive jurisdiction.
    </p>

    <p><strong>16. Contact</strong></p>
    <p>
      Questions or concerns should be sent to:
      <a href="mailto:hello@anddine.co.uk">hello@anddine.co.uk</a>
    </p>
  </section>
</template>

<style lang="scss">
.website-terms {
  width: 90%;
  padding: 50px 5%;
  background-color: var(--color-white-1);
  margin-top: 80px;
  p {
    color: var(--color-grey-1);
  }
  h1 {
    color: var(--color-grey-1);
  }
  ul {
    list-style-type: disc;
    margin: 10px 0;
    padding-left: 20px;
    color: var(--color-grey-1);
    li {
      margin-bottom: 4px;
    }
  }
}
</style>
